/**
 * OwnerChatService
 *
 * Handles Help Genie chat sessions and messages via the brain-chat edge function.
 * sendMessage enqueues a job and receives events via Supabase Realtime.
 */

import { supabase } from "@/integrations/supabase/client";
import { ImpersonationService } from "@/services/users/ImpersonationService";

import type {
  BrainChatMessage,
  BrainChatSession,
  BrainChatResponse,
  SendBrainMessageParams,
  ToolCallSummary,
} from "@/types/brain";

const FUNCTION_NAME = "brain-chat";

export interface StreamCallbacks {
  onDelta?: (text: string) => void;
  onToolCall?: (tool: string) => void;
  onStatus?: (text: string) => void;
  onToolResult?: (tool: string, summary: string) => void;
  onDone?: (toolCallsMade: ToolCallSummary[]) => void;
  onError?: (error: string) => void;
}

export class OwnerChatService {
  static async createSession(): Promise<string> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "create-session",
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error(`Failed to create session: ${error.message}`);
    if (!data?.ok) throw new Error(data?.error ?? "Failed to create session");
    return data.session_id;
  }

  static async getSessions(): Promise<BrainChatSession[]> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "get-sessions",
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error(`Failed to get sessions: ${error.message}`);
    return data?.sessions ?? [];
  }

  static async deleteSession(sessionId: string): Promise<void> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "delete-session",
        session_id: sessionId,
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error(`Failed to delete session: ${error.message}`);
    if (!data?.ok) throw new Error(data?.error ?? "Failed to delete session");
  }

  static async getMessages(sessionId: string): Promise<BrainChatMessage[]> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "get-messages",
        session_id: sessionId,
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error(`Failed to get messages: ${error.message}`);
    return data?.messages ?? [];
  }

  /**
   * Send a message and receive events via Supabase Realtime.
   * Enqueues a job on brain-chat, subscribes to brain_chat_events for that job,
   * and dispatches the same StreamCallbacks interface as before.
   */
  static async sendMessageStream(params: SendBrainMessageParams, callbacks: StreamCallbacks): Promise<void> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();

    // 1. Create job row — get job_id before subscribing so we don't miss events
    const { data: prepareData, error: prepareError } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "prepare-message",
        session_id: params.session_id,
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (prepareError || !prepareData?.ok) throw new Error(prepareError?.message ?? prepareData?.error ?? "Failed to prepare message");

    const jobId: string = prepareData.job_id;

    // 2. Subscribe FIRST, fire worker only once channel is SUBSCRIBED
    await new Promise<void>((resolve, reject) => {
      // Realtime's INSERT delivery order isn't guaranteed to match insertion
      // (seq) order, so out-of-order delta events are briefly buffered and
      // replayed by seq — otherwise streamed text can arrive scrambled even
      // though the DB-persisted message (fetched separately) is correct.
      // Realtime can also miss a notification outright (most commonly the
      // very first INSERT right after SUBSCRIBED, before the replication
      // slot has caught up) — a missing seq must not be treated as "still
      // coming": a short debounce instead backfills via a direct SELECT for
      // any row(s) the client hasn't dispatched yet, so a dropped event is
      // replayed instead of silently lost.
      let nextSeq = 0;
      let backfillChain: Promise<void> = Promise.resolve();
      const pending = new Map<number, { type: string; payload: Record<string, unknown> }>();
      const dispatchedSeqs = new Set<number>();
      let flushTimer: ReturnType<typeof setTimeout> | null = null;

      const applyRow = (row: { seq: number; type: string; payload: Record<string, unknown> }) => {
        if (dispatchedSeqs.has(row.seq)) return;
        pending.set(row.seq, { type: row.type, payload: row.payload });
      };

      const drainContiguous = () => {
        while (pending.has(nextSeq)) {
          const next = pending.get(nextSeq)!;
          pending.delete(nextSeq);
          dispatchedSeqs.add(nextSeq);
          nextSeq += 1;
          dispatch(next.type, next.payload);
        }
      };

      // Chained (not guarded by a busy flag) so every caller — the debounce
      // timer and the terminal finalize() — actually waits for a fresh
      // SELECT reflecting the latest nextSeq, instead of one skipping
      // because another backfill happened to be in flight.
      const backfillOnce = async () => {
        const { data, error } = await supabase
          .from("brain_chat_events")
          .select("seq, type, payload")
          .eq("job_id", jobId)
          .gte("seq", nextSeq)
          .order("seq", { ascending: true });
        if (!error && data) {
          for (const row of data) {
            applyRow(row as { seq: number; type: string; payload: Record<string, unknown> });
          }
          drainContiguous();
        }
      };

      const backfill = (): Promise<void> => {
        backfillChain = backfillChain.then(backfillOnce);
        return backfillChain;
      };

      // One backfill attempt isn't always enough — the missing row may not
      // have committed yet even by the time the SELECT runs. Keep retrying
      // on a short interval until the gap (nextSeq) actually closes, instead
      // of giving up after a single attempt and leaving the gap for `done`
      // to paper over — by then the client has already rendered around it.
      const scheduleFlush = () => {
        if (flushTimer !== null) return;
        const attempt = () => {
          flushTimer = null;
          void backfill().then(() => {
            if (pending.size > 0) {
              flushTimer = setTimeout(attempt, 200);
            }
          });
        };
        flushTimer = setTimeout(attempt, 200);
      };

      // Runs on the terminal (done/error) event: backfills once more so any
      // seq gap still open at stream end (e.g. a missed early delta) is
      // replayed before the terminal callback fires, instead of being lost.
      const finalize = async (fireTerminalCallback: () => void) => {
        await backfill();
        fireTerminalCallback();
        supabase.removeChannel(channel);
        resolve();
      };

      const dispatch = (type: string, eventPayload: Record<string, unknown>) => {
        switch (type) {
          case "delta":
            callbacks.onDelta?.(eventPayload.text as string);
            break;
          case "tool_call":
            callbacks.onToolCall?.(eventPayload.tool as string);
            break;
          case "status":
            callbacks.onStatus?.(eventPayload.text as string);
            break;
          case "tool_result":
            callbacks.onToolResult?.(eventPayload.tool as string, eventPayload.summary as string);
            break;
          case "done":
            if (flushTimer !== null) {
              clearTimeout(flushTimer);
              flushTimer = null;
            }
            void finalize(() => callbacks.onDone?.((eventPayload.tool_calls_made ?? []) as ToolCallSummary[]));
            break;
          case "error":
            if (flushTimer !== null) {
              clearTimeout(flushTimer);
              flushTimer = null;
            }
            void finalize(() => callbacks.onError?.((eventPayload.error as string) ?? "Unknown error"));
            break;
        }
      };

      const channel = supabase
        .channel(`brain-chat-${jobId}`)
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "brain_chat_events",
            filter: `job_id=eq.${jobId}`,
          },
          (payload) => {
            const { type, seq } = payload.new as { type: string; payload: Record<string, unknown>; seq: number };
            const eventPayload = (payload.new as any).payload ?? {};

            applyRow({ seq, type, payload: eventPayload });
            drainContiguous();
            if (pending.size > 0) {
              scheduleFlush();
            } else if (flushTimer !== null) {
              clearTimeout(flushTimer);
              flushTimer = null;
            }
          },
        )
        .subscribe((status) => {
          if (status === "SUBSCRIBED") {
            // 3. Channel is live — now fire the worker; it won't miss any events
            supabase.functions.invoke(FUNCTION_NAME, {
              body: {
                action: "send-message",
                job_id: jobId,
                message: params.message,
                session_id: params.session_id,
                ...(params.scope ? { scope: params.scope } : {}),
                ...(params.diagnostic ? { diagnostic: true } : {}),
                ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
              },
            }).catch((err: Error) => {
              callbacks.onError?.(err.message ?? "Failed to send message");
              supabase.removeChannel(channel);
              resolve();
            });
          } else if (status === "CHANNEL_ERROR") {
            reject(new Error("Realtime channel error"));
          }
        });
    });
  }

  /** Submit thumbs up/down feedback on an assistant message */
  static async submitFeedback(
    messageId: string,
    feedback: { rating: "positive" | "negative"; text?: string },
  ): Promise<void> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "submit-feedback",
        message_id: messageId,
        feedback: { ...feedback, created_at: new Date().toISOString() },
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error("Failed to submit feedback");
  }

  /** Non-streaming fallback (kept for compatibility) */
  static async sendMessage(params: SendBrainMessageParams): Promise<BrainChatResponse> {
    const impersonatedUserId = ImpersonationService.getImpersonatedUserId();
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME, {
      body: {
        action: "send-message",
        message: params.message,
        session_id: params.session_id,
        ...(impersonatedUserId ? { adminMode: true, impersonatedUserId } : {}),
      },
    });
    if (error) throw new Error(`Failed to send message: ${error.message}`);
    if (!data?.ok) throw new Error(data?.error ?? "Failed to respond");
    return data as BrainChatResponse;
  }
}
